import client from './client';

export type Regulator = 'FCA' | 'ESMA' | 'ASIC' | 'MAS' | 'CIRO' | 'FSCA' | 'JFSA' | 'CySEC';

export type RegulatoryTopic =
  | 'trade-transaction-reporting'
  | 'retail-brokers-distribution'
  | 'cfd-spread-betting'
  | 'new-rules-policy-statements';

export interface RegulatoryNewsArticle {
  id: string;
  regulator: Regulator;
  regulatorLabel: string;
  title: string;
  summary: string;
  whyThisMatters: string;
  topics: RegulatoryTopic[];
  sourceUrl: string;
  sourceName: string;
  publishedDate: { _seconds: number; _nanoseconds: number } | string;
  ingestedAt: { _seconds: number; _nanoseconds: number } | string;
  ingestSource: 'rss' | 'scrape' | 'backfill-curated';
  reviewStatus: 'pending_verification' | 'approved' | 'declined';
  /** Assessed not relevant: shown under the "Not relevant" toggle, not the main feed. */
  hidden?: boolean;
  hiddenReason?: 'auto-irrelevant' | 'manual' | null;
}

export const REGULATORS: { value: Regulator; label: string }[] = [
  { value: 'FCA', label: 'FCA' },
  { value: 'ESMA', label: 'ESMA' },
  { value: 'ASIC', label: 'ASIC' },
  { value: 'MAS', label: 'MAS' },
  { value: 'CIRO', label: 'CIRO' },
  { value: 'FSCA', label: 'FSCA' },
  { value: 'JFSA', label: 'JFSA' },
  { value: 'CySEC', label: 'CySEC' },
];

export const TOPICS: { value: RegulatoryTopic; label: string }[] = [
  { value: 'trade-transaction-reporting', label: 'Trade Reporting' },
  { value: 'retail-brokers-distribution', label: 'Brokers' },
  { value: 'cfd-spread-betting', label: 'CFDs/SBs' },
  { value: 'new-rules-policy-statements', label: 'Policy/Guidance' },
];

export const toDate = (value: RegulatoryNewsArticle['publishedDate']): Date => {
  if (typeof value === 'string') return new Date(value);
  return new Date(value._seconds * 1000);
};

export const fetchRegulatoryNews = async (): Promise<RegulatoryNewsArticle[]> => {
  const { data } = await client.get<{ articles: RegulatoryNewsArticle[] }>('/regulatory-news');
  return data.articles;
};

export const hideRegulatoryNewsArticle = (id: string): Promise<void> =>
  client.patch(`/regulatory-news/${encodeURIComponent(id)}/hide`).then(() => undefined);

export const unhideRegulatoryNewsArticle = (id: string): Promise<void> =>
  client.patch(`/regulatory-news/${encodeURIComponent(id)}/unhide`).then(() => undefined);

export interface RegulatoryNewsUsageRecord {
  id: string;
  timestamp: { _seconds: number; _nanoseconds: number } | string;
  regulator: Regulator;
  articleTitle: string;
  model: string;
  promptTokens: number;
  candidateTokens: number;
  thoughtsTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  generatedBy: 'gemini' | 'fallback';
}

export const fetchRegulatoryNewsUsage = async (params: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<RegulatoryNewsUsageRecord[]> => {
  const { data } = await client.get<{ records: RegulatoryNewsUsageRecord[] }>('/regulatory-news/usage', {
    params: { date_from: params.dateFrom, date_to: params.dateTo },
  });
  return data.records;
};

export const generateNewsletter = (articleIds: string[], criteriaLabel: string): Promise<Blob> =>
  client
    .post('/regulatory-news/newsletter', { articleIds, criteriaLabel }, { responseType: 'blob' })
    .then((res) => res.data);

export const generateCarousel = (articleIds: string[], criteriaLabel: string): Promise<Blob> =>
  client
    .post('/regulatory-news/carousel', { articleIds, criteriaLabel }, { responseType: 'blob' })
    .then((res) => res.data);

export interface RegulatoryNewsExport {
  id: string;
  type: 'newsletter' | 'carousel';
  criteriaLabel: string;
  articleCount: number;
  createdBy: string;
  createdAt: { _seconds: number; _nanoseconds: number } | string;
}

export const fetchRegulatoryNewsExports = async (): Promise<RegulatoryNewsExport[]> => {
  const { data } = await client.get<{ exports: RegulatoryNewsExport[] }>('/regulatory-news/exports');
  return data.exports;
};

export const fetchRegulatoryNewsExportPdf = (id: string): Promise<Blob> =>
  client
    .get(`/regulatory-news/exports/${encodeURIComponent(id)}`, { responseType: 'blob' })
    .then((res) => res.data);
